import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { post, get } from "../../services/ApiService";
import "../custom-style.css";
import Loader from "../../components/Loader";
import toast, { Toaster } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import { PRODUCT_FORMATS } from "../../utils/productFormats";

const ProductAdd = () => {
  const backend_ul = process.env.REACT_APP_API_ENDPOINT;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [variations, setVariations] = useState([createEmptyVariation()]);
  const [vatCategories, setVatCategories] = useState([]);
  const [allPostages, setAllPostages] = useState([]);
  const [postageCodes, setPostageCodes] = useState([]);
  const [product, setProduct] = useState({
    title: "",
    itemId: "",
    type: "",
    enableEbayMessage: false,
    ebayMessageContent: "",
    purchaseLimitDays: "",
    platform: "Ebay", // Default platform
  });
  
  const [showModal, setShowModal] = useState(false);
  const [currentVariationIndex, setCurrentVariationIndex] = useState(null);
  const [variationDetails, setVariationDetails] = useState({
    goodsDescription: "",
    hsCode: "",
    extendedGoodDescription: "",
  });

  const riskLevels = Array.from({ length: 70 - 14 + 1 }, (_, i) => i + 14);

  // Function to create empty variation
  function createEmptyVariation() {
    return {
      SKU: "",
      name: "",
      format: "",
      weight: "",
      length: "",
      width: "",
      height: "",
      limit: "",
      riskLevel: "",
      location: "MI Health Longcroft House",
      postage: [],
      flatPacked: false,
      extraProtection: false,
      packagingType: "",
      preferredBrand: "",
      goodsDescription: "",
      hsCode: "",
      extendedGoodDescription: "",
    };
  }

  // Get filtered postages based on current platform
  const getFilteredPostages = () => {
    return allPostages.filter(
      (postage) => postage.type === product.platform || postage.type === "All"
    );
  };

  // Fetch necessary data for the form
  const fetchFormData = async () => {
    try {
      setIsDataLoading(true);
      const res = await get('api/products/entities');
      if (res.success) {
        setVatCategories(res.vatCategories || []);
        setAllPostages(res.postages || []);
        setPostageCodes(res.postageCodes || []);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast.error("Failed to load form data");
      setIsDataLoading(false);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    fetchFormData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct({
      ...product,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      
    //   const formData = new FormData();
    //   formData.append("title", product.title);
    //   formData.append("type", product.type);
    //   formData.append("platform", product.platform);
    //   formData.append("enableEbayMessage", product.enableEbayMessage);
    //   formData.append("ebayMessageContent", product.ebayMessageContent);
    //   formData.append("purchaseLimitDays", product.purchaseLimitDays);

    //   formData.append("variations", JSON.stringify(variations));

      const payload = {
        product,
        variations
      };

      const res = await post("api/products/create", payload);
      
      if (res.success) {
        toast.success("Product created successfully!");
        navigate(`/products/edit/${res.data._id}`);
      } else {
        toast.error(res.error || "Failed to create product");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVarInputChange = (index, field, value) => {
    const newVariations = [...variations];
    newVariations[index][field] = value;
    
    // Auto-generate SKU if name changes and SKU is empty
    if (field === "name" && (!newVariations[index].SKU || newVariations[index].SKU === "")) {
      const sku = value.replace(/\s+/g, '-').toUpperCase().substring(0, 20);
      newVariations[index].SKU = sku;
    }
    
    setVariations(newVariations);
  };

  const handleVarPostageChange = (varIndex, postageIndex, newValue, postageName) => {
    const updatedVariations = [...variations];
    if (!updatedVariations[varIndex].postage) {
      updatedVariations[varIndex].postage = [];
    }
    
    // Find the postage object to get the name
    const postage = getFilteredPostages()[postageIndex];
    
    updatedVariations[varIndex].postage[postageIndex] = {
      ...updatedVariations[varIndex].postage[postageIndex],
      name: postage.name,
      code: newValue,
    };
    setVariations(updatedVariations);
  };

  const addVariation = () => {
    setVariations([...variations, createEmptyVariation()]);
  };

  const removeVariation = (index) => {
    if (variations.length > 1) {
      const newVariations = variations.filter((_, i) => i !== index);
      setVariations(newVariations);
    } else {
      toast.error("At least one variation is required");
    }
  };

  const handleShowModal = (index, variation) => {
    setCurrentVariationIndex(index);
    setVariationDetails({
      goodsDescription: variation.goodsDescription || "",
      hsCode: variation.hsCode || "",
      extendedGoodDescription: variation.extendedGoodDescription || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    setVariationDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveVariationDetails = () => {
    if (currentVariationIndex !== null) {
      const newVariations = [...variations];
      newVariations[currentVariationIndex] = {
        ...newVariations[currentVariationIndex],
        ...variationDetails,
      };
      setVariations(newVariations);
      handleCloseModal();
      toast.success("Variation details saved!");
    }
  };

  return (
    <Layout>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
        <div className="row mb-9 align-items-center">
          <div className="col-xxl-9">
            <div className="row">
              <div className="col-sm-6 mb-8 mb-sm-0">
                <h2 className="fs-4 mb-0 bd-heading">Add New Product</h2>
                <Breadcrumbs />
              </div>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="row">
              <div className="col-lg-12">
                <div className="card mb-8 rounded-4">
                  <div className="card-body p-7">
                    {isDataLoading && (
                      <div className="text-center">
                        <Loader />
                      </div>
                    )}
                    
                    <form className="form-border-1" onSubmit={handleSubmit}>
                      {/* Product Basic Information */}
                      <div className="mb-8">
                        <label htmlFor="product_title" className="mb-4 fs-13px ls-1 fw-bold text-uppercase">
                          Product Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          placeholder="Type product title here"
                          className="form-control"
                          value={product.title}
                          onChange={handleInputChange}
                          id="product_title"
                          required
                        />
                      </div>

                      <div className="mb-8">
                        <label htmlFor="itemId" className="mb-4 fs-13px ls-1 fw-bold text-uppercase">
                          Item ID
                        </label>
                        <input
                          type="text"
                          name="itemId"
                          placeholder="Enter Item ID"
                          className="form-control"
                          value={product.itemId}
                          onChange={handleInputChange}
                          id="itemId"
                        />
                      </div>

                      <div className="mb-8">
                        <label className="mb-4 fs-13px ls-1 fw-bold text-uppercase" htmlFor="type">
                          Choose Product Type
                        </label>
                        <select
                          className="form-select"
                          name="type"
                          value={product.type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Choose Product Type</option>
                          <option value="GSL">GSL</option>
                          <option value="P-Med">P-Med</option>
                        </select>
                      </div>

                      <div className="mb-8">
                        <label className="mb-4 fs-13px ls-1 fw-bold text-uppercase" htmlFor="platform">
                          Platform
                        </label>
                        <select
                          className="form-select"
                          name="platform"
                          value={product.platform}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="Ebay">eBay</option>
                          <option value="Amazon">Amazon</option>
                          <option value="GroupOn">GroupOn</option>
                          <option value="Website">Website</option>
                        </select>
                      </div>

                      <div className="mb-4">
                        <div className="d-flex align-items-center">
                          <input
                            className="form-check-input rounded-0 ms-0"
                            type="checkbox"
                            name="enableEbayMessage"
                            checked={product.enableEbayMessage}
                            onChange={handleInputChange}
                          />
                          <div style={{ marginLeft: "10px" }}>
                            <label htmlFor="enableEbayMessage">
                              Enable Message on Purchase
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-8">
                        <label htmlFor="ebayMessageContent" className="mb-4 fs-13px ls-1 fw-bold text-uppercase">
                          Message Content
                        </label>
                        <textarea
                          className="form-control"
                          rows={7}
                          placeholder="Write your message here..."
                          value={product.ebayMessageContent}
                          onChange={handleInputChange}
                          name="ebayMessageContent"
                        />
                      </div>               
                      
                      <div className="mb-8">
                        <label htmlFor="purchaseLimitDays" className="mb-4 fs-13px ls-1 fw-bold text-uppercase">
                          Purchase Limit Days
                        </label>
                        <input
                          type="number"
                          name="purchaseLimitDays"
                          placeholder="Enter days limit"
                          className="form-control"
                          value={product.purchaseLimitDays}
                          onChange={handleInputChange}
                          id="purchaseLimitDays"
                        />
                      </div>

                      {/* Variations Section */}
                      <div className="mb-8">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h4 className="mb-0">Product Variations</h4>
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={addVariation}
                          >
                            <i className="fa fa-plus me-2"></i>Add Variation
                          </button>
                        </div>
                        
                        <div className="row">
                          {variations.map((variation, index) => (
                            <div className="col-lg-6 mb-4" key={index}>
                              <div className="card border">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0">Variation {index + 1}</h6>
                                  {variations.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      onClick={() => removeVariation(index)}
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  )}
                                </div>
                                <div className="card-body">
                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">SKU</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={variation.SKU}
                                      onChange={(e) => handleVarInputChange(index, "SKU", e.target.value)}
                                      placeholder="SKU"
                                      required
                                    />
                                  </div>
                                  
                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Name</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={variation.name}
                                      onChange={(e) => handleVarInputChange(index, "name", e.target.value)}
                                      placeholder="Variation name"
                                      
                                    />
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Format</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={variation.format}
                                      onChange={(e) => handleVarInputChange(index, "format", e.target.value)}
                                      required
                                    >
                                      <option value="">Choose Format</option>
                                      {PRODUCT_FORMATS.map((formatOption) => (
                                        <option key={formatOption} value={formatOption}>
                                          {formatOption}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="row">
                                    <div className="col-6 mb-3">
                                      <label className="form-label small fw-bold">Weight (g)</label>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={variation.weight}
                                        onChange={(e) => handleVarInputChange(index, "weight", e.target.value)}
                                        
                                      />
                                    </div>
                                    <div className="col-6 mb-3">
                                      <label className="form-label small fw-bold">Length (cm)</label>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={variation.length}
                                        onChange={(e) => handleVarInputChange(index, "length", e.target.value)}
                                        
                                      />
                                    </div>
                                  </div>

                                  <div className="row">
                                    <div className="col-6 mb-3">
                                      <label className="form-label small fw-bold">Width (cm)</label>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={variation.width}
                                        onChange={(e) => handleVarInputChange(index, "width", e.target.value)}
                                        
                                      />
                                    </div>
                                    <div className="col-6 mb-3">
                                      <label className="form-label small fw-bold">Height (cm)</label>
                                      <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={variation.height}
                                        onChange={(e) => handleVarInputChange(index, "height", e.target.value)}
                                        
                                      />
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Limit</label>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={variation.limit}
                                      onChange={(e) => handleVarInputChange(index, "limit", e.target.value)}
                                    />
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Preferred Brand</label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={variation.preferredBrand}
                                      onChange={(e) => handleVarInputChange(index, "preferredBrand", e.target.value)}
                                      placeholder="Enter preferred brand"
                                    />
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Packaging Type</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={variation.packagingType}
                                      onChange={(e) => handleVarInputChange(index, "packagingType", e.target.value)}
                                    >
                                      <option value="">Choose Packaging Type</option>
                                      <option value="Small Mailing Bag">Small Mailing Bag</option>
                                      <option value="Medium Mailing Bag">Medium Mailing Bag</option>
                                      <option value="Large Mailing Bag">Large Mailing Bag</option>
                                      <option value="Small Jiffy Bag C/0">Small Jiffy Bag C/0</option>
                                      <option value="Medium Jiffy Bag F/3">Medium Jiffy Bag F/3</option>
                                      <option value="Large Jiffy Bag J/6">Large Jiffy Bag J/6</option>
                                      <option value="Small Box">Small Box</option>
                                      <option value="Medium Box">Medium Box</option>
                                      <option value="Large Box">Large Box</option>
                                      <option value="C4 PIP Boxes">C4 PIP Boxes</option>
                                      <option value="C5 PIP Boxes">C5 PIP Boxes</option>
                                      <option value="C6 PIP Boxes">C6 PIP Boxes</option>
                                    </select>
                                  </div>

                                  <div className="mb-3">
                                    <label className="form-label small fw-bold">Location</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={variation.location}
                                      onChange={(e) => handleVarInputChange(index, "location", e.target.value)}
                                      required
                                    >
                                      <option value="MI Health Longcroft House">MI Health Longcroft House</option>
                                      <option value="Berkeley Care Unit">Berkeley Care Unit</option>
                                    </select>
                                  </div>

                                  <div className="mb-3">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={variation.flatPacked}
                                        onChange={(e) => handleVarInputChange(index, "flatPacked", e.target.checked)}
                                      />
                                      <label className="form-check-label small fw-bold">
                                        Flat Packed
                                      </label>
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={variation.extraProtection}
                                        onChange={(e) => handleVarInputChange(index, "extraProtection", e.target.checked)}
                                      />
                                      <label className="form-check-label small fw-bold">
                                        Extra Protection
                                      </label>
                                    </div>
                                  </div>

                                  {/* Postage Section */}
                                  {getFilteredPostages().length > 0 && (
                                    <div className="mb-3">
                                      <label className="form-label small fw-bold">Postage Options</label>
                                      {getFilteredPostages().map((postage, pIndex) => (
                                        <div key={pIndex} className="mb-2">
                                          <label className="form-label small">
                                            {postage.name} <span className="text-muted">({postage.type})</span>
                                          </label>
                                          <select
                                            className="form-select form-select-sm"
                                            value={variation.postage?.[pIndex]?.code || ""}
                                            onChange={(e) => handleVarPostageChange(index, pIndex, e.target.value, postage.name)}
                                          >
                                            <option value="">Choose {postage.name} Code</option>
                                            {postageCodes.map((item, cIndex) => (
                                              <option key={cIndex} value={item.code}>
                                                {item.code}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm w-100 mt-2"
                                    onClick={() => handleShowModal(index, variation)}
                                  >
                                    <i className="fa fa-ellipsis-h me-2"></i>
                                    Additional Details
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="col-sm-12 d-flex flex-wrap justify-content-sm-end gap-2">
                        <Link to="/products" className="btn btn-secondary">
                          Cancel
                        </Link>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isLoading}
                        >
                          {isLoading && <i className="fa fa-spinner fa-spin me-2"></i>}
                          Create Product
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Variation Details Modal */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Variation Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Goods Description</label>
              <input
                type="text"
                className="form-control"
                name="goodsDescription"
                value={variationDetails.goodsDescription}
                onChange={handleDetailChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">HS Code</label>
              <input
                type="text"
                className="form-control"
                name="hsCode"
                value={variationDetails.hsCode}
                onChange={handleDetailChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Extended Good Description</label>
              <textarea
                className="form-control"
                name="extendedGoodDescription"
                value={variationDetails.extendedGoodDescription}
                onChange={handleDetailChange}
                rows={3}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary" onClick={saveVariationDetails}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default ProductAdd;